import React, { useState, useRef, useEffect } from "react";
import "./Register.css";
import { auth } from "../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { generateId } from "../utils/generateId";

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    subdistrict: "",
    password: "",
    confirmPassword: ""
  });

  const [lang, setLang] = useState("en");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showSubdistrict, setShowSubdistrict] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const [facingMode, setFacingMode] = useState("environment");

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    if (showCamera) startCamera();
    else stopCamera();
  }, [showCamera, facingMode]);

  useEffect(() => {
    const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "normal"
    });
    verifier.render();
    recaptchaRef.current = verifier;
    window.recaptchaVerifier = verifier;

    return () => {
      try { verifier.clear(); } catch (_) {}
      recaptchaRef.current = null;
      window.recaptchaVerifier = null;
    };
  }, []);

  const text = {
    en: {
      title: "Create Account",
      register: "Register",
      name: "Full Name",
      email: "Email",
      phone: "Phone Number",
      address: "Address",
      subdistrict: "Sub District",
      password: "Password",
      confirm: "Confirm Password"
    },
    kn: {
      title: "ಖಾತೆ ರಚಿಸಿ",
      register: "ನೋಂದಣಿ",
      name: "ಪೂರ್ಣ ಹೆಸರು",
      email: "ಇಮೇಲ್",
      phone: "ಫೋನ್ ನಂಬರ್",
      address: "ವಿಳಾಸ",
      subdistrict: "ಉಪ ಜಿಲ್ಲೆ",
      password: "ಪಾಸ್‌ವರ್ಡ್",
      confirm: "ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ"
    }
  };

  const subdistricts = [
    "Mandya", "Maddur", "Malavalli", "Srirangapatna",
    "Nagamangala", "Krishnarajapete", "Pandavapura"
  ];

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch {
      alert("Camera permission denied");
    }
  };

  const stopCamera = () => {
    const video = videoRef.current;
    if (!video?.srcObject) return;
    video.srcObject.getTracks().forEach(t => t.stop());
    video.srcObject = null;
  };

  const flipCamera = () => {
    setFacingMode(prev => prev === "environment" ? "user" : "environment");
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    setPreview(canvas.toDataURL("image/png"));
    // Stop all tracks immediately so the camera indicator light turns off
    const stream = video.srcObject;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      video.srcObject = null;
    }
    setShowCamera(false);
  };

  const handleGallery = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
    setShowImageOptions(false);
  };

  const closeAll = () => {
    setShowSubdistrict(false);
    setShowImageOptions(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (form.password !== form.confirmPassword) {
      alert("Passwords do not match");
      return;
    }
    if (!form.phone || form.phone.length < 10) {
      alert("Enter a valid 10-digit phone number");
      return;
    }
    if (!form.email) {
      alert("Enter email");
      return;
    }

    try {
      setLoading(true);

      const customId = await generateId("USR");

      localStorage.setItem("tempUser", JSON.stringify({
        id: customId,
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        subdistrict: form.subdistrict,
        photoURL: preview,
        password: form.password
      }));

      const phoneNumber = "+91" + form.phone.replace(/\D/g, "").slice(-10);
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaRef.current);
      window.confirmationResult = confirmation;

      navigate("/otp");
    } catch(err) {
      // Reset widget so user can re-tick and retry
      try { recaptchaRef.current?.clear(); } catch (_) {}
      recaptchaRef.current = null;
      window.recaptchaVerifier = null;
      const verifier = new RecaptchaVerifier(auth, "recaptcha-container", { size: "normal" });
      verifier.render();
      recaptchaRef.current = verifier;
      window.recaptchaVerifier = verifier;
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container" onClick={closeAll}>
      <div className="register-header" onClick={(e) => e.stopPropagation()}>
        <div className="lang-toggle">
          <button onClick={() => setLang("en")}>EN</button>
          <button onClick={() => setLang("kn")}>ಕನ್ನಡ</button>
        </div>

        <div
          className="camera-circle"
          onClick={(e) => {
            e.stopPropagation();
            setShowImageOptions(true);
          }}
        >
          {preview ? <img src={preview} alt="" className="profile-img" /> : "📷"}
        </div>

        <h2>{text[lang].title}</h2>
      </div>

      <form className="register-form" onSubmit={handleRegister} onClick={(e) => e.stopPropagation()}>
        <div className="input-box"><span>👤</span>
          <input placeholder={text[lang].name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>

        <div className="input-box"><span>✉️</span>
          <input type="email" placeholder={text[lang].email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        </div>

        <div className="input-box"><span>📞</span>
          <input type="tel" maxLength="10" placeholder={text[lang].phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
        </div>

        <div className="input-box"><span>📍</span>
          <input placeholder={text[lang].address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>

        <div className="input-box dropdown" onClick={() => setShowSubdistrict(!showSubdistrict)}>
          <span>📍</span>
          <input readOnly value={form.subdistrict} placeholder={text[lang].subdistrict} />
          <span>▼</span>
          {showSubdistrict && (
            <div className="dropdown-menu">
              {subdistricts.map((item, i) => (
                <div
                  key={i}
                  className={`dropdown-item ${form.subdistrict === item ? "active" : ""}`}
                  onClick={() => { setForm({ ...form, subdistrict: item }); setShowSubdistrict(false); }}
                >
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="input-box">
          <span>🔒</span>
          <input type={showPass ? "text" : "password"} placeholder={text[lang].password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          <button type="button" onClick={() => setShowPass(!showPass)}>👁️</button>
        </div>

        <div className="input-box">
          <span>🔒</span>
          <input type={showConfirm ? "text" : "password"} placeholder={text[lang].confirm}
            onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} required />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)}>👁️</button>
        </div>

        <div id="recaptcha-container"></div>

        <button className="register-btn" disabled={loading}>
          {loading ? "Sending OTP..." : text[lang].register}
        </button>
      </form>

      {showImageOptions && (
        <div className="image-options" onClick={() => setShowImageOptions(false)}>
          <div className="image-box" onClick={(e) => e.stopPropagation()}>
            <button style={{ color: "black" }}
              onClick={() => { setShowCamera(true); setShowImageOptions(false); }}>
              📷 Take Photo
            </button>
            <button style={{ color: "black" }}
              onClick={() => document.getElementById("galleryInput").click()}>
              🖼️ Choose from Gallery
            </button>
          </div>
        </div>
      )}

      {showCamera && (
        <div className="camera-overlay">
          <video ref={videoRef} autoPlay playsInline className="camera-video" />
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <div className="camera-controls">
            <button onClick={() => document.getElementById("galleryInput").click()}>🖼️</button>
            <button onClick={flipCamera}>🔄</button>
            <button onClick={capturePhoto}>📸</button>
            <button onClick={() => setShowCamera(false)}>❌</button>
          </div>
        </div>
      )}

      <input type="file" id="galleryInput" accept="image/*" onChange={handleGallery} hidden />
    </div>
  );
}
